import { Box, Grid, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import useStaticIPs from "../../../../api/hooks/useStaticIPs";
import { integrationKeys } from "../../../../api/queryKeys";
import LoadingPrimaryButton from "../../../../components/buttons/LoadingPrimaryButton.jsx";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import BreezeStaticIPsModal from "../components/BreezeStaticIPsModal";
import Guideline from "../components/Guideline";
import GuidelineInstruction from "../components/GuidelineInstruction";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import snowflake_account_info from "./images/snowflake_account_info.png";
import snowflake_connect_tool from "./images/snowflake_connect_tool.png";
import snowflake_create_sql_worksheet from "./images/snowflake_create_sql_worksheet.png";
import snowflake_run_sql_command from "./images/snowflake_run_sql_command.png";
import snowflake_select_account_role from "./images/snowflake_select_account_role.png";
import snowflake_switch_account_role from "./images/snowflake_switch_account_role.png";
import snowflake_user_details from "./images/snowflake_user_details.png";
import SnowflakeConnectForm from "./SnowflakeConnectForm";
import SqlCommandDisplay from "./SqlCommandDisplay";
import getSnowflakeCreateUserAndRoleSqlCommand from "./SqlCommands";

const SnowflakeIntegration = () => {
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);
  const [loading, setLoading] = useState(false);
  const [snowflakeUserPublicKey, setSnowflakeUserPublicKey] = useState("");
  const [showStaticIPsModal, setShowStaticIPsModal] = useState(false);
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("snowflake");
  const queryClient = useQueryClient();
  const { staticIPs: allowlistIps } = useStaticIPs();

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerSnowflakeIntegration, generateSnowflakeKMSkey } = useAPI();

  useEffect(() => {
    setLoading(isIntegratedLoading);
  }, [isIntegratedLoading]);

  const handleGenerateKey = async () => {
    await generateSnowflakeKMSkey()
      .then((response) => {
        setSnowflakeUserPublicKey(response.data);
        Toast.success("Key generated successfully");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      });
  };

  const onSubmit = async (values) => {
    setLoading(true);
    try {
      await registerSnowflakeIntegration(values.account_url, values.username);
      queryClient.invalidateQueries({
        queryKey: integrationKeys.all,
      });
      Toast.success("Snowflake integration successful");
    } catch (error) {
      if (error.response) {
        Toast.error(`Something went wrong: ${error.response.data.detail}`);
      } else {
        Toast.error("An error occurred while processing the request.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated && !loading
            ? "Once integrated, Breeze will scan your Snowflake for databases and collections to correlate security gaps with your business assets."
            : ""
        }
        image={logos.SnowflakeLogo}
        iconBorderRadius={0}
        name={"Snowflake"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>Snowflake is integrated!</Typography>
            </Box>
          ) : (
            <div>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton
                      onClick={() => setShowStaticIPsModal(true)}
                    >
                      View Breeze Static IPs
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>

              {/* SETUP INSTRUCTIONS */}
              <GuidelineStepByStep>
                <GuidelineStep title="Login to your Snowflake console">
                  <GuidelineInstruction text="Make sure you have an ACCOUNTADMIN role - required for the next steps." />
                  <PrimaryButton
                    onClick={() => {
                      window.open(`https://app.snowflake.com/`, "_blank");
                    }}
                    style={{ textTransform: "none", width: "300px" }}
                  >
                    Login to Snowflake
                  </PrimaryButton>
                  <GuidelineInstruction text="Ensure you are using the ACCOUNTADMIN role. If not, you can switch the role from the bottom left side." />
                  <ScreenshotAccordion
                    images={[
                      {
                        file: snowflake_switch_account_role,
                        altext: "Snowflake switch to account admin role",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Open a new SQL worksheet">
                  <GuidelineInstruction text="Go to “Projects“ > “Worksheets“ and create a new SQL worksheet." />
                  <ScreenshotAccordion
                    images={[
                      {
                        file: snowflake_create_sql_worksheet,
                        altext: "Snowflake create SQL worksheet",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <GuidelineInstruction text="At the top right, select the role ACCOUNTADMIN and the warehouse you want Breeze will be used." />
                  <Typography variant="body2">
                    <strong>Note:</strong> Make sure to select an <b>x-small</b>{" "}
                    warehouse to avoid extra cost.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: snowflake_select_account_role,
                        altext: "Snowflake select account admin role",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Generate a new KeyPair for the new user.">
                  <GuidelineInstruction text="We need to generate a new key pair for the new user." />
                  <GuidelineInstruction text="We'll generate an new pair of keys using AWS KMS. (RSA 4096 bit keys)" />
                  <LoadingPrimaryButton
                    style={{ textTransform: "none", width: "300px" }}
                    disabled={snowflakeUserPublicKey}
                    onClick={async () => {
                      await handleGenerateKey();
                    }}
                  >
                    {!snowflakeUserPublicKey
                      ? "Generate Public/Private key pair"
                      : "Key generated"}
                  </LoadingPrimaryButton>
                </GuidelineStep>
                <GuidelineStep title="Create a new user and role">
                  <GuidelineInstruction
                    text={
                      <Box
                        sx={{ display: "flex", flexDirection: "row", gap: 1 }}
                      >
                        <Typography variant={"subtitle1"}>
                          Copy and run the following SQL command.
                        </Typography>
                        <CustomCopyButton
                          copyTitle={"SQL command"}
                          copyText={getSnowflakeCreateUserAndRoleSqlCommand(
                            snowflakeUserPublicKey
                          )}
                          disabled={!snowflakeUserPublicKey}
                        />
                      </Box>
                    }
                  />
                  <SqlCommandDisplay
                    sqlCommand={
                      snowflakeUserPublicKey
                        ? getSnowflakeCreateUserAndRoleSqlCommand(
                            snowflakeUserPublicKey
                          )
                        : "You must generate a key pair first"
                    }
                    isSql={snowflakeUserPublicKey}
                  />
                  <GuidelineInstruction text="You can run the command by pasting it in the SQL worksheet clicking the “Run“ button." />
                  <ScreenshotAccordion
                    images={[
                      {
                        file: snowflake_run_sql_command,
                        altext: "Snowflake run SQL command",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />{" "}
                  <GuidelineInstruction text="This command creates a new user: “BreezeUser“ and a new role “BreezeSecurityReadOnly“." />
                  <GuidelineInstruction text="The role “BreezeSecurityReadOnly“ get IMPORTED PRIVILEGES on the database -" />
                  <GuidelineInstruction text="Which will allow the role access to ACCOUNT_USAGE schema." />
                  <GuidelineInstruction text="You should see the properties of the new user at the result." />
                  <ScreenshotAccordion
                    images={[
                      {
                        file: snowflake_user_details,
                        altext: "Snowflake user details",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />{" "}
                </GuidelineStep>
                <GuidelineStep title="Add Breeze's IP addresses to your Network Policy (Optional)">
                  <GuidelineInstruction text="Add the following IP addresses to your Snowflake network policy allowed IPs list:" />
                  {allowlistIps.map((ip, index) => (
                    <Box
                      key={index}
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{
                        margin: 0,
                        padding: "4px 0",
                        justifyContent: "space-between",
                        width: "170px",
                      }}
                    >
                      <span>{ip}</span>
                      <CustomCopyButton
                        copyTitle={"IP Address"}
                        copyText={ip}
                      />
                    </Box>
                  ))}
                </GuidelineStep>
                <GuidelineStep title="Fill in the user details">
                  <GuidelineInstruction text="In this section, please enter the required information to connect to your Snowflake account." />
                  <GuidelineInstruction text="You can find your “account URL“ By clicking on your user in the bottom left corner." />
                  <GuidelineInstruction text="Click on “Connect a tool to Snowflake“" />
                  <ScreenshotAccordion
                    images={[
                      {
                        file: snowflake_connect_tool,
                        altext: "Snowflake connect tool",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />{" "}
                  <GuidelineInstruction text="Copy the “Account/Server URL“ field" />
                  <ScreenshotAccordion
                    images={[
                      {
                        file: snowflake_account_info,
                        altext: "Snowflake account info",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />{" "}
                  <GuidelineInstruction text="Fill the user details down below." />
                  <GuidelineInstruction text="The “Username“ should be “BreezeUser“ (Leave blank if you didn't change the SQL)." />
                  <GuidelineInstruction text="If you did change the username make sure to copy the username from the result table since it's case sensitive." />
                  <SnowflakeConnectForm
                    onSubmit={onSubmit}
                    guideMode={guideMode}
                  />
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
      <BreezeStaticIPsModal
        open={showStaticIPsModal}
        onClose={() => setShowStaticIPsModal(false)}
      />
    </Box>
  );
};

export default SnowflakeIntegration;
